import * as React from "react";
import { Box, Typography, TextField } from "@mui/material";
import DarkTextField from "../DarkTextField";
import DocumentUploadList from "./DocumentUploadList";
import { AUTH_TEXT_PRIMARY, AUTH_TEXT_SECONDARY, BRAND_BLUE } from "../../theme/appTheme";
import { useTranslation } from "../../i18n/LanguageContext";

const portalFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontSize: "0.8125rem",
    bgcolor: "#F3F4F6",
    "& fieldset": { borderColor: "#F3F4F6" },
    "&:hover fieldset": { borderColor: "#d8d8d8" },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#6b7280",
  },
};

// Labeled field matching this app's established "form in a settings/admin
// dialog" look (label above, plain outlined input) - used for the "portal"
// variant instead of the auth screens' placeholder-only DarkTextField.
function PortalField({ label, helperText, boxSx, ...rest }) {
  return (
    <Box sx={{ mb: 2, ...boxSx }}>
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "#374151", mb: 0.75 }}>{label}</Typography>
      <TextField fullWidth size="small" variant="outlined" sx={portalFieldSx} {...rest} />
      {helperText && (
        <Typography sx={{ fontSize: "0.6875rem", color: AUTH_TEXT_SECONDARY, mt: 0.5 }}>{helperText}</Typography>
      )}
    </Box>
  );
}

// Practice details + certificate upload - the fields POSTed to
// /account/therapist-apply. Shared by the registration "register as
// therapist" toggle (variant="auth", the default) and Account Settings'
// apply/re-apply flow (variant="portal").
function TherapistApplyFields({ value, onChange, disabled = false, variant = "auth" }) {
  const { t } = useTranslation();
  const { practiceName, practiceCity, practiceEmail, files } = value;

  const setField = (field) => (e) => onChange({ ...value, [field]: e.target.value });

  if (variant === "portal") {
    return (
      <>
        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#111827", mb: 0.25 }}>
          {t("auth.therapistApply.practiceDetails")}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY, mb: 2 }}>
          {t("auth.therapistApply.reviewNotice")}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
          <PortalField
            label={t("auth.therapistApply.practiceName")}
            placeholder={t("auth.therapistApply.practiceNamePlaceholderPortal")}
            value={practiceName}
            onChange={setField("practiceName")}
            disabled={disabled}
            boxSx={{ mb: 0 }}
          />
          <PortalField
            label={t("auth.therapistApply.practiceCity")}
            placeholder={t("auth.therapistApply.practiceCityPlaceholderPortal")}
            value={practiceCity}
            onChange={setField("practiceCity")}
            disabled={disabled}
            boxSx={{ mb: 0 }}
          />
        </Box>
        <PortalField
          label={t("auth.therapistApply.practiceEmail")}
          value={practiceEmail}
          disabled={disabled}
          InputProps={{ readOnly: true }}
          helperText={t("auth.therapistApply.practiceEmailHelp")}
        />

        <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", color: "#111827", mb: 1 }}>
          {t("auth.therapistApply.licenseCertificates")}
        </Typography>
        <DocumentUploadList files={files} onChange={(next) => onChange({ ...value, files: next })} disabled={disabled} />
      </>
    );
  }

  return (
    <>
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: AUTH_TEXT_PRIMARY, mb: 0.25 }}>
        {t("auth.therapistApply.practiceDetails")}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY, mb: 1.5 }}>
        {t("auth.therapistApply.reviewNotice")}
      </Typography>

      <DarkTextField
        value={practiceName}
        onChange={setField("practiceName")}
        placeholder={t("auth.therapistApply.practiceName")}
        disabled={disabled}
        sx={{ mb: 1.5 }}
      />
      <DarkTextField
        value={practiceCity}
        onChange={setField("practiceCity")}
        placeholder={t("auth.therapistApply.practiceCity")}
        disabled={disabled}
        sx={{ mb: 1.5 }}
      />
      <DarkTextField
        value={practiceEmail}
        placeholder={t("auth.therapistApply.practiceEmail")}
        disabled={disabled}
        InputProps={{ readOnly: true }}
        sx={{ mb: 0.5 }}
      />
      <Typography sx={{ fontSize: "0.6875rem", color: AUTH_TEXT_SECONDARY, mb: 2 }}>
        {t("auth.therapistApply.practiceEmailHelp")}
      </Typography>

      <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", color: AUTH_TEXT_PRIMARY, mb: 1 }}>
        {t("auth.therapistApply.licenseCertificates")}
      </Typography>
      <DocumentUploadList files={files} onChange={(next) => onChange({ ...value, files: next })} disabled={disabled} />
    </>
  );
}

export default TherapistApplyFields;
